import DOMPurify from 'dompurify'

export const utf8ToGsm0338 = (utf8: string): string => {
  const regex = /â|ê|ô|€/gi
  let result = utf8
  if (regex.test(utf8)) {
    result = utf8.replace(regex, '')
  }
  return result
}

export const isHtmlEmpty = (html: string | null | undefined): boolean => {
  if (!html) return true
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()
  return text.length === 0
}

// Remote images are dropped and media tags removed: loading them would report the opening
// (and the reader's IP) to the sender, the way tracking pixels do. Inline and attached images are kept.
const EMBEDDED_IMAGE = /^\s*(?:data:image\/|cid:)/i

const UNSAFE_STYLE_DECLARATION =
  /^\s*(position|z-index|top|right|bottom|left|inset|behavior|-moz-binding)\s*:|url\s*\(|expression\s*\(|@import/i

const CID_PREFIX = /^\s*cid:/i

let inlineImageUrls = new Map<string, string>()

const decodeContentId = (encodedContentId: string): string => {
  try {
    return decodeURIComponent(encodedContentId)
  } catch {
    return encodedContentId
  }
}

const emailPurifier = DOMPurify()
emailPurifier.addHook('uponSanitizeAttribute', (node, data) => {
  if (
    node.nodeName === 'IMG' &&
    data.attrName === 'src' &&
    CID_PREFIX.test(data.attrValue)
  ) {
    const contentId = decodeContentId(
      data.attrValue.replace(CID_PREFIX, '').trim()
    )
    const inlineImageUrl = inlineImageUrls.get(contentId)
    if (inlineImageUrl) {
      data.attrValue = inlineImageUrl
      return
    }
  }
  if (data.attrName === 'style') {
    data.attrValue = data.attrValue
      .split(';')
      .filter(
        (declaration) =>
          declaration.trim() !== '' &&
          !UNSAFE_STYLE_DECLARATION.test(declaration)
      )
      .join(';')
  }
  if (
    node.nodeName === 'IMG' &&
    (data.attrName === 'src' || data.attrName === 'srcset') &&
    !EMBEDDED_IMAGE.test(data.attrValue)
  ) {
    data.keepAttr = false
  }
})
emailPurifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName === 'A' && node.hasAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

export const sanitizeEmailHtml = (
  html: string,
  inlineImages = new Map<string, string>()
): string => {
  inlineImageUrls = inlineImages
  try {
    return sanitizeWithEmailPurifier(html)
  } finally {
    inlineImageUrls = new Map()
  }
}

const sanitizeWithEmailPurifier = (html: string): string =>
  emailPurifier.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: [
      'form',
      'input',
      'button',
      'select',
      'textarea',
      'style',
      'video',
      'audio',
      'source',
      'track',
      'picture'
    ],
    FORBID_ATTR: ['class', 'id'],
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|ftp|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
    FORCE_BODY: true
  })

export const normalizeSearchString = (value: string): string =>
  value
    .normalize('NFD')
    // Remove diacritics
    .replace(/\p{M}/gu, '')
    .toLowerCase()
